import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Settings as SettingsIcon, Upload, Image as ImageIcon, PenLine } from 'lucide-react';

const Settings = () => {
  const businessId = localStorage.getItem('currentBusiness');
  const [business, setBusiness] = useState(null);
  const [formData, setFormData] = useState({});
  const [bankDetails, setBankDetails] = useState({ accountName: '', ifscCode: '', accountNumber: '', bankName: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingSig, setUploadingSig] = useState(false);

  useEffect(() => { fetchBusiness(); }, []);

  const fetchBusiness = async () => {
    try {
      const response = await api.get(`/api/business/${businessId}`);
      const biz = response.data.business;
      setBusiness(biz);
      setFormData(biz);
      if (biz.bankDetails) setBankDetails(biz.bankDetails);
    } catch (error) {
      console.error('Failed to fetch business:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/api/business/${businessId}`, { ...formData, bankDetails });
      toast.success('Settings updated successfully');
      fetchBusiness();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please upload an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image size should be less than 5MB'); return; }

    const setUploading = field === 'logo' ? setUploadingLogo : setUploadingSig;
    setUploading(true);
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        await api.put(`/api/business/${businessId}`, { [field]: reader.result });
        setFormData(prev => ({ ...prev, [field]: reader.result }));
        toast.success(`${field === 'logo' ? 'Logo' : 'Signature'} uploaded successfully`);
      } catch (error) {
        toast.error(`Failed to upload ${field}`);
      } finally {
        setUploading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold font-heading flex items-center gap-3">
          <SettingsIcon className="w-8 h-8" />
          Settings
        </h1>
        <p className="text-sm text-gray-600 mt-1">Manage your business configuration</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Logo & Signature */}
        <Card>
          <CardHeader><CardTitle>Branding</CardTitle></CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Logo */}
              <div>
                <Label className="mb-2 block font-semibold">Business Logo</Label>
                <div className="flex items-center gap-4">
                  {formData.logo ? (
                    <img src={formData.logo} alt="Logo" className="w-24 h-24 object-contain border rounded-lg" />
                  ) : (
                    <div className="w-24 h-24 border-2 border-dashed rounded-lg flex items-center justify-center bg-gray-50">
                      <ImageIcon className="w-10 h-10 text-gray-400" />
                    </div>
                  )}
                  <div>
                    <Label htmlFor="logo-upload" className="cursor-pointer">
                      <div className="flex items-center gap-2 px-3 py-2 border rounded-lg hover:bg-gray-50 transition-colors text-sm">
                        <Upload className="w-4 h-4" />
                        {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                      </div>
                      <Input id="logo-upload" type="file" accept="image/*" onChange={(e) => handleFileUpload(e, 'logo')} className="hidden" disabled={uploadingLogo} />
                    </Label>
                    <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 5MB</p>
                  </div>
                </div>
              </div>
              {/* Signature */}
              <div>
                <Label className="mb-2 block font-semibold">Authorized Signature</Label>
                <div className="flex items-center gap-4">
                  {formData.signature ? (
                    <img src={formData.signature} alt="Signature" className="w-24 h-16 object-contain border rounded-lg" />
                  ) : (
                    <div className="w-24 h-16 border-2 border-dashed rounded-lg flex items-center justify-center bg-gray-50">
                      <PenLine className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                  <div>
                    <Label htmlFor="sig-upload" className="cursor-pointer">
                      <div className="flex items-center gap-2 px-3 py-2 border rounded-lg hover:bg-gray-50 transition-colors text-sm">
                        <Upload className="w-4 h-4" />
                        {uploadingSig ? 'Uploading...' : 'Upload Signature'}
                      </div>
                      <Input id="sig-upload" type="file" accept="image/*" onChange={(e) => handleFileUpload(e, 'signature')} className="hidden" disabled={uploadingSig} />
                    </Label>
                    <p className="text-xs text-gray-500 mt-1">Transparent PNG recommended</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Business Information */}
        <Card>
          <CardHeader><CardTitle>Business Information</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Business Name</Label>
                <Input id="name" value={formData.name || ''} onChange={(e) => setFormData({ ...formData, name: e.target.value })} data-testid="business-name-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="registrationNo">Registration / License No.</Label>
                <Input id="registrationNo" value={formData.registrationNo || ''} onChange={(e) => setFormData({ ...formData, registrationNo: e.target.value })} placeholder="e.g. 31-B-NUBRA" data-testid="business-reg-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input id="phone" value={formData.phone || ''} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} data-testid="business-phone-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={formData.email || ''} onChange={(e) => setFormData({ ...formData, email: e.target.value })} data-testid="business-email-input" />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="address">Address</Label>
                <Input id="address" value={formData.address || ''} onChange={(e) => setFormData({ ...formData, address: e.target.value })} data-testid="business-address-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">State</Label>
                <Input id="state" value={formData.state || ''} onChange={(e) => setFormData({ ...formData, state: e.target.value })} data-testid="business-state-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="gstin">GSTIN</Label>
                <Input id="gstin" value={formData.gstin || ''} onChange={(e) => setFormData({ ...formData, gstin: e.target.value })} data-testid="business-gstin-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pan">PAN</Label>
                <Input id="pan" value={formData.pan || ''} onChange={(e) => setFormData({ ...formData, pan: e.target.value })} data-testid="business-pan-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="upiId">UPI ID</Label>
                <Input id="upiId" value={formData.upiId || ''} onChange={(e) => setFormData({ ...formData, upiId: e.target.value })} placeholder="business@paytm" data-testid="business-upi-input" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bank Details */}
        <Card>
          <CardHeader><CardTitle>Bank Details (for Invoice)</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="accountName">Account Name</Label>
                <Input id="accountName" value={bankDetails.accountName || ''} onChange={(e) => setBankDetails({ ...bankDetails, accountName: e.target.value })} placeholder="Account holder name" data-testid="bank-account-name-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="accountNumber">Account Number</Label>
                <Input id="accountNumber" value={bankDetails.accountNumber || ''} onChange={(e) => setBankDetails({ ...bankDetails, accountNumber: e.target.value })} placeholder="0000000000000" data-testid="bank-account-number-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ifscCode">IFSC Code</Label>
                <Input id="ifscCode" value={bankDetails.ifscCode || ''} onChange={(e) => setBankDetails({ ...bankDetails, ifscCode: e.target.value })} placeholder="ABCD0001234" data-testid="bank-ifsc-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bankName">Bank Name & Branch</Label>
                <Input id="bankName" value={bankDetails.bankName || ''} onChange={(e) => setBankDetails({ ...bankDetails, bankName: e.target.value })} placeholder="State Bank of India, Main Branch" data-testid="bank-name-input" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Invoice Configuration */}
        <Card>
          <CardHeader><CardTitle>Invoice Configuration</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="invoicePrefix">Invoice Prefix</Label>
                <Input id="invoicePrefix" value={formData.invoicePrefix || ''} onChange={(e) => setFormData({ ...formData, invoicePrefix: e.target.value })} data-testid="invoice-prefix-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="financialYear">Financial Year</Label>
                <Input id="financialYear" value={formData.financialYear || ''} onChange={(e) => setFormData({ ...formData, financialYear: e.target.value })} data-testid="financial-year-input" />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button type="submit" disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid="save-settings-button">
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default Settings;
